import { Button } from '@/components/ui/button'
import { pointCount } from '@/lib/map-point-edit'

import type { MapPointEditing } from './use-map-point-editing'

function hintText(editing: MapPointEditing): string {
  if (editing.handleStatus === 'zoom-in') return 'Zoom in to edit points.'
  if (editing.insertBlockedReason != null) {
    return `Can't insert a point here: ${editing.insertBlockedReason}.`
  }
  if (editing.selectedCount > 0) {
    return `${pointCount(editing.selectedCount)} selected. Drag one to move them all; Delete removes them; Esc clears.`
  }
  return 'Drag a point to move it. Click the line to insert one. Ctrl+click adds or removes a point, Shift+click selects the points in between, Shift+drag selects a box.'
}

/**
 * The on-map bar shown in point-editing mode: what to do next (or why
 * handles/insertion aren't available here), and the actions for the
 * current selection. Every map gesture has a keyboard/button counterpart
 * here except the drag itself and selecting points, which need a pointer.
 */
export function MapEditToolbar({
  editing,
  onDone,
}: Readonly<{ editing: MapPointEditing; onDone: () => void }>) {
  const hasSelection = editing.selectedCount > 0
  const actionsDisabled = !hasSelection || editing.busy
  return (
    <div
      role="toolbar"
      aria-label="Point editing"
      className="border-primary bg-popover text-popover-foreground absolute top-2.5 right-12 left-12 z-10 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-2 py-1.5 text-xs shadow-md"
    >
      <p aria-live="polite" className="min-w-0 flex-1 basis-48">
        {hintText(editing)}
      </p>
      <div className="flex flex-wrap items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2"
          disabled={actionsDisabled}
          title={
            hasSelection
              ? 'Remove only the GPS position; time, heart rate and other data stay'
              : 'Select points first'
          }
          onClick={() => void editing.removeGpsOnlyForSelection()}
        >
          Remove GPS only
        </Button>
        <Button
          size="sm"
          variant="destructive"
          className="h-7 px-2"
          disabled={actionsDisabled}
          title={hasSelection ? 'Delete the whole record(s), not just GPS' : 'Select points first'}
          onClick={() => void editing.deleteSelection()}
        >
          Delete {hasSelection ? pointCount(editing.selectedCount) : 'points'}
        </Button>
        {hasSelection && (
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={editing.clearSelection}>
            Clear selection
          </Button>
        )}
        <Button size="sm" variant="default" className="h-7 px-2" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  )
}
